import type { DetectorProfile } from "@/components/domain/detector-profile"
import type { ComboboxOption } from "@/components/ui/combobox"
import { ApiError } from "@/lib/errors"
import type { TableFetchResult, TableQueryParams, TableSort } from "@/lib/table"

export type ClientId = string & { readonly __brand: "ClientId" }
export type UserId = string & { readonly __brand: "UserId" }

export interface ClientMember {
  userId: UserId
  name: string
  email: string
  isPrimary: boolean
}

export interface PrimaryClientContact {
  userId: UserId
  name: string
  status: "active" | "deactivated" | "viewOnly" | "notStaff" | "unresolved"
}

export type UnroutablePrimaryClientContactStatus = Exclude<
  PrimaryClientContact["status"],
  "active" | "unresolved"
>

export const UNROUTABLE_PRIMARY_CLIENT_CONTACT_LABELS: Record<
  UnroutablePrimaryClientContactStatus,
  string
> = {
  deactivated: "Deactivated",
  viewOnly: "View only",
  notStaff: "Not staff",
}

export interface ClientRow {
  id: ClientId
  name: string
  primaryClientContactId: UserId | null
  notes: string | null
  isActive: boolean
  createdAt: string
  members: ClientMember[]
  reviewCount: number
  openReviewCount: number
  /** What Automatic resolves to first for this engagement's reviews. Empty is all Automatic. */
  detectorProfile: DetectorProfile
}

export interface ClientUserOption {
  id: UserId
  name: string
  email: string
}

export type StaffOption = ClientUserOption

export function toPersonPickerOption(person: ClientUserOption): ComboboxOption {
  return { value: person.id, label: `${person.name} — ${person.email}` }
}

export interface CreateClientInput {
  name: string
  primaryClientContactId: UserId | null
  notes: string | null
}

export const PRIMARY_CLIENT_CONTACT_NO_LONGER_ACTIVE =
  "That person is no longer active. Pick someone else."

export type NewEngagementField = "name" | "primaryClientContact"

export class NewEngagementFieldError extends Error {
  readonly field: NewEngagementField

  constructor(field: NewEngagementField, message: string) {
    super(message)
    this.name = "NewEngagementFieldError"
    this.field = field
  }
}

export function isPrimaryClientContactNoLongerActiveError(
  error: unknown,
  sentPrimaryClientContactId: UserId | null,
): boolean {
  return sentPrimaryClientContactId !== null && error instanceof ApiError && error.status === 404
}

export interface ClientFilters {
  status?: "active" | "inactive"
  hasPrimary?: boolean
  createdAt?: { from: string | null; to: string | null }
}

export interface ClientQueryParams extends Omit<TableQueryParams, "filters"> {
  filters: ClientFilters
}

const SEARCHABLE = (r: ClientRow): string =>
  [r.name, r.notes ?? "", ...r.members.flatMap((mem) => [mem.name, mem.email])]
    .join(" ")
    .toLowerCase()

const SORTERS: Record<string, (a: ClientRow, b: ClientRow) => number> = {
  name: (a, b) => a.name.localeCompare(b.name),
  reviewCount: (a, b) => a.reviewCount - b.reviewCount,
  members: (a, b) => a.members.length - b.members.length,
  createdAt: (a, b) => a.createdAt.localeCompare(b.createdAt),
}

function withinRange(iso: string, range: { from: string | null; to: string | null }): boolean {
  if (range.from && iso < range.from) return false
  if (range.to && iso > `${range.to}T23:59:59.999Z`) return false
  return true
}

function matches(r: ClientRow, filters: ClientFilters, needle: string): boolean {
  if (filters.status === "active" && !r.isActive) return false
  if (filters.status === "inactive" && r.isActive) return false
  if (filters.hasPrimary === true && r.primaryClientContactId === null) return false
  if (filters.hasPrimary === false && r.primaryClientContactId !== null) return false
  if (filters.createdAt && !withinRange(r.createdAt, filters.createdAt)) return false
  if (needle && !SEARCHABLE(r).includes(needle)) return false
  return true
}

function sort(rows: readonly ClientRow[], s: TableSort | null): ClientRow[] {
  if (!s) return [...rows]
  const cmp = SORTERS[s.field]
  if (!cmp) return [...rows]
  const dir = s.dir === "asc" ? 1 : -1
  return [...rows].sort((a, b) => cmp(a, b) * dir)
}

/**
 * Client-side filter / sort / paginate over the rows the API returned.
 * A pure function so `clients-page.tsx` can compose it without owning any
 * fetch state, and unit tests can exercise the query independently of the
 * transport layer.
 */
export function queryClients(
  rows: readonly ClientRow[],
  params: ClientQueryParams,
): TableFetchResult<ClientRow> {
  const needle = params.search.trim().toLowerCase()
  const filtered = rows.filter((r) => matches(r, params.filters, needle))
  const sorted = sort(filtered, params.sort)
  const start = (params.page - 1) * params.pageSize
  return { rows: sorted.slice(start, start + params.pageSize), total: sorted.length }
}

/**
 * Filter (only) — used when server pagination has already sliced the rows and
 * only the local axes (status, hasPrimary, createdAt range) need to apply on top.
 */
export function filterClients(rows: readonly ClientRow[], filters: ClientFilters): ClientRow[] {
  return rows.filter((r) => matches(r, filters, ""))
}
